import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ServicePage.css';

import softwareDesignImage from '../../assets/images/software/design-main.jpg';
import softwareDigitalMarketingImage from '../../assets/images/software/digital-marketing.jpg';
import softwareAfterSalesImage from '../../assets/images/software/after-sales.jpg';
import softwareWebDevelopmentImage from '../../assets/images/software/web-development.jpg';
import softwareWebScrappingImage from '../../assets/images/software/web-scrapping.jpg';
import softwareAndroidImage from '../../assets/images/software/android-development.jpg';
import softwareARVRImage from '../../assets/images/software/VR.jpg';

interface ServicePageProps {
  serviceName: string;
  serviceDescription: string;
  videoUrl?: string;
  productImages: string[];
  features: string[];
  benefits: string[];
  benefitsImage?: string;
}

const softwareServices = [
  {
    id: 'software-design',
    nameKey: 'servicePage.services.softwareDesign.name',
    image: softwareDesignImage,
    route: '/software-design'
  },
  {
    id: 'software-digital-marketing',
    nameKey: 'servicePage.services.softwareDigitalMarketing.name',
    image: softwareDigitalMarketingImage,
    route: '/software-digital-marketing'
  },
  {
    id: 'software-after-sales',
    nameKey: 'servicePage.services.softwareAfterSales.name',
    image: softwareAfterSalesImage,
    route: '/software-after-sales'
  },
  {
    id: 'software-web-development',
    nameKey: 'servicePage.services.softwareWebDevelopment.name',
    image: softwareWebDevelopmentImage,
    route: '/software-web-development'
  },
  {
    id: 'software-web-scrapping',
    nameKey: 'servicePage.services.softwareWebScrapping.name',
    image: softwareWebScrappingImage,
    route: '/software-web-scrapping'
  },
  {
    id: 'software-android-development',
    nameKey: 'servicePage.services.softwareAndroidDevelopment.name',
    image: softwareAndroidImage,
    route: '/software-android-development'
  },
  {
    id: 'software-ar-vr',
    nameKey: 'servicePage.services.softwareARVR.name',
    image: softwareARVRImage,
    route: '/software-ar-vr'
  }
];

const ServicePage: React.FC<ServicePageProps> = ({
  serviceName,
  serviceDescription,
  videoUrl,
  productImages: _productImages,
  features,
  benefits,
  benefitsImage
}) => {
  const { t } = useTranslation();
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'features' | 'benefits'>('overview');
  const [isMobile, setIsMobile] = useState(false);
  const [currentServiceGroup, setCurrentServiceGroup] = useState(0);

  const relatedServices = React.useMemo(() => {
    return softwareServices.filter((service) => t(service.nameKey) !== serviceName);
  }, [serviceName, t]);

  const baseVisibleCount = isMobile ? 1 : 4;
  const totalGroups = relatedServices.length
    ? Math.max(1, Math.ceil(relatedServices.length / baseVisibleCount))
    : 0;
  const canNavigate = totalGroups > 1;

  const visibleServices = React.useMemo(() => {
    if (!relatedServices.length) return [] as typeof relatedServices;
    const count = Math.min(baseVisibleCount, relatedServices.length);
    const startIndex = canNavigate ? (currentServiceGroup * baseVisibleCount) % relatedServices.length : 0;
    const items = [] as typeof relatedServices;
    for (let i = 0; i < count; i++) {
      const index = (startIndex + i) % relatedServices.length;
      items.push(relatedServices[index]);
      if (!canNavigate && index === relatedServices.length - 1) {
        break;
      }
    }
    return items;
  }, [relatedServices, currentServiceGroup, baseVisibleCount, canNavigate]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Intersection Observer for scroll animations - runs after tab changes
  useEffect(() => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Small delay to ensure DOM is updated after tab change
    const timeoutId = setTimeout(() => {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible');
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      // Observe all fade-in sections
      const sections = document.querySelectorAll('.fade-in-section');
      sections.forEach((section) => {
        // Reset visibility for tab panels
        if (section.classList.contains('tab-panel')) {
          section.classList.remove('visible');
        }
        observerRef.current?.observe(section);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [activeTab]); // Re-run when tab changes

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  // Track viewport size to toggle mobile carousel mode
  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  useEffect(() => {
    setCurrentServiceGroup(0);
  }, [isMobile, relatedServices.length, serviceName]);

  const showPreviousService = () => {
    if (!canNavigate || !relatedServices.length) return;
    setCurrentServiceGroup((prev) => (prev - 1 + totalGroups) % totalGroups);
  };

  const showNextService = () => {
    if (!canNavigate || !relatedServices.length) return;
    setCurrentServiceGroup((prev) => (prev + 1) % totalGroups);
  };

  return (
    <div className="service-page-new">
      {/* Hero Section */}
      <section className={`hero-section ${!videoUrl ? 'hero-section-no-video' : ''}`}>
        {videoUrl && (
          <div className="hero-video-wrapper">
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              className="hero-video"
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="hero-video-overlay"></div>
          </div>
        )}
        <div className="hero-content">
          <div className="container">
            <nav className="breadcrumb-nav">
              <Link to="/" className="breadcrumb-link">{t('servicePage.breadcrumb.home')}</Link>
              <span className="breadcrumb-separator">/</span>
              <Link to="/services" className="breadcrumb-link">{t('servicePage.breadcrumb.services')}</Link>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">{serviceName}</span>
            </nav>
            <div className="hero-text">
              <h1 className="hero-title">{serviceName}</h1>
              <p className="hero-subtitle">{serviceDescription}</p>
              <div className="hero-actions">
                <Link to="/contact" className="btn-primary-hero">
                  {t('servicePage.benefits.getQuote')}
                </Link>
                <Link to="/contact" className="btn-secondary-hero">
                  {t('servicePage.benefits.scheduleConsultation')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Tabs Section */}
      <section className="content-tabs-section">
        <div className="container">
          <div className="tabs-header">
            <button
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              {t('common.overview')}
            </button>
            <button
              className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
              onClick={() => setActiveTab('features')}
            >
              {t('servicePage.features.title')}
            </button>
            <button
              className={`tab-btn ${activeTab === 'benefits' ? 'active' : ''}`}
              onClick={() => setActiveTab('benefits')}
            >
              {t('servicePage.benefits.title')}
            </button>
          </div>
          <div className="tabs-content">
            {activeTab === 'overview' && (
              <div className="tab-panel tab-panel-active" id="overview-panel">
                <div className={`overview-content ${!benefitsImage ? 'overview-content-single' : ''}`}>
                  <div className="overview-text">
                    <h2>{t('common.aboutThisService')}</h2>
                    <p>{serviceDescription}</p>
                    <p>{t('common.defaultServiceDescription')}</p>
                  </div>
                  {benefitsImage && (
                    <div className="overview-image">
                      <img src={benefitsImage} alt={serviceName} loading="lazy" />
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'features' && (
              <div className="tab-panel tab-panel-active" id="features-panel">
                <div className="features-grid-new">
                  {features.map((feature, index) => (
                    <div key={index} className="feature-card-new" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="feature-icon-new">
                        <div className="feature-number-new">{index + 1}</div>
                      </div>
                      <h3 className="feature-title-new">{feature}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {activeTab === 'benefits' && (
              <div className="tab-panel tab-panel-active" id="benefits-panel">
                <div className="benefits-grid-new">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="benefit-card-new" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="benefit-check-icon">✓</div>
                      <p className="benefit-text-new">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Other Services Section */}
      {relatedServices.length > 0 && (
        <section className="products-gallery-section fade-in-section" id="products-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title-new">{t('servicePage.otherServices.title', 'Other Services')}</h2>
              <p className="section-subtitle">{t('servicePage.otherServices.subtitle', 'Discover more of our software expertise')}</p>
            </div>
            <div className="product-carousel product-carousel-multi">
              <button
                type="button"
                className="carousel-arrow carousel-arrow-left"
                onClick={showPreviousService}
                aria-label={t('common.previousServices')}
                disabled={!canNavigate}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <div className="product-carousel-track">
                {visibleServices.map((service, index) => (
                  <Link
                    key={`${service.id}-${index}`}
                    to={service.route}
                    className="product-carousel-card other-service-item"
                  >
                    <div className="product-image-wrapper">
                      <img src={service.image} alt={t(service.nameKey)} loading="lazy" />
                      <div className="product-card-title">{t(service.nameKey)}</div>
                    </div>
                  </Link>
                ))}
              </div>
              <button
                type="button"
                className="carousel-arrow carousel-arrow-right"
                onClick={showNextService}
                aria-label={t('common.nextServices')}
                disabled={!canNavigate}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              {canNavigate && (
                <div className="product-carousel-dots">
                  {Array.from({ length: totalGroups }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`product-carousel-dot ${index === currentServiceGroup ? 'active' : ''}`}
                      onClick={() => setCurrentServiceGroup(index)}
                      aria-label={`Show group ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default ServicePage;
